/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import moment from 'moment';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardDynamicComponent, StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectCurrentDeviceFeatures, selectPasswordChange, selectPasswordEnabled } from 'src/app/store/device/device.selector';
import { DeviceFeature } from 'src/app/model/device-feature';

@Component({
	selector: 'app-password-edit-dialog',
	templateUrl: './password-edit-dialog.component.html',
	providers: [{
		provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true }
	}]
})

export class PasswordEditDialogComponent implements OnInit, OnDestroy {
	passwordForm: FormGroup;
	passwordChange$: Observable<boolean | undefined>;
	passwordChangeSub: Subscription | undefined;
	passwordEnabled$: Observable<boolean | undefined>;
	passwordEnabledSub: Subscription | undefined;
	deviceFeature$: Observable<DeviceFeature[] | undefined>;
	deviceFeatureSub: Subscription | undefined;
	passwordChange: boolean;
	passwordEnabled: boolean;

	minLenghtPassword: number;
	lengthPassword: number;

	enabledPassword: boolean = false;
	disableConfirm: boolean = false;

	newPassInvalid: boolean = false;

	// design
	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
	) {
		this.initSelectors();
	}


	initSelectors(): void {
		// Store selectors
		this.passwordChange$ = this.store.select(selectPasswordChange);
		this.deviceFeature$ = this.store.select(selectCurrentDeviceFeatures);
		this.passwordEnabled$ = this.store.select(selectPasswordEnabled);
	}


	ngOnInit(): void {
		this.minLenghtPassword = 3;
		this.lengthPassword = 10;
		this.subscribeAll();
		this.buildForm();

		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	subscribeAll() {
		this.passwordChangeSub = this.passwordChange$.subscribe(passwordChange => {
			this.passwordChange = passwordChange;
			if (this.passwordChange) {
				this.store.dispatch(DeviceActions.passwordVerifySupport({ password: '' }));
				this.closeDialog();
			}
		});
		this.passwordEnabledSub = this.passwordEnabled$.subscribe(passwordEnabled => {
			this.passwordEnabled = passwordEnabled;
			this.buildForm();
			this.enablePassSwitch();
		});
		this.deviceFeatureSub = this.deviceFeature$.subscribe(deviceFeatures => {
			const fea = deviceFeatures.find(fe => fe.Name === 'password');
			if (fea !== undefined) {
				this.lengthPassword = fea.Size;
				this.buildForm();
			}
		});
	}

	buildForm() {
		this.passwordForm = new FormGroup({
			'PasswordOld': new FormControl('', [Validators.minLength(this.minLenghtPassword), Validators.maxLength(this.lengthPassword), Validators.pattern('[0-9]+')]),
			'PasswordNew': new FormControl('', [Validators.minLength(this.minLenghtPassword), Validators.maxLength(this.lengthPassword), Validators.pattern('[0-9]+')]),
			'enabledPassword': new FormControl(this.passwordEnabled),
		});
	}

	ngOnDestroy(): void {
		this.passwordChangeSub?.unsubscribe();
		this.deviceFeatureSub?.unsubscribe();
		this.passwordEnabledSub?.unsubscribe();
	}

	enablePassSwitch() {
		this.enabledPassword = this.passwordForm.get("enabledPassword").value;
		if (!this.passwordEnabled) { // i need to activatePassword
			if (!this.enabledPassword) { // switch is off
				this.disableConfirm = true;
			} else {
				this.disableConfirm = false;
			}
		}
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		const passwordOld = this.f.PasswordOld.value;
		const passwordNew = this.f.PasswordNew.value;
		this.newPassInvalid = false;
		if (!this.passwordEnabled) { // i want to activate password
			if (passwordNew === '') {
				this.passwordForm.controls["PasswordNew"].setErrors({ required: true });
			}
		}
		if (this.passwordEnabled) { // i want to switch off password or change it
			if (passwordOld === '') { // required for switch off or change
				this.passwordForm.controls["PasswordOld"].setErrors({ required: true });
			}
			if (this.enabledPassword) { // i'm changing password
				if (passwordNew === '') {
					this.passwordForm.controls["PasswordNew"].setErrors({ required: true });
				} else {
					if (passwordNew === passwordOld) { // error, same password as old
						this.passwordForm.controls["PasswordNew"].setErrors({ invalid: true });
						this.newPassInvalid = true;
					}
				}
			}
		}
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.passwordForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.passwordForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	save(): void {
		let passwordOld = this.f.PasswordOld.value;
		let passwordNew = this.f.PasswordNew.value;
		if (this.passwordEnabled && !this.f.enabledPassword.value) {
			passwordNew = '';
		}
		if (!this.passwordEnabled && this.f.enabledPassword.value) {
			passwordOld = '';
		}
		this.store.dispatch(DeviceActions.passwordChange({
			passwordOld: passwordOld,
			passwordNew: passwordNew
		}));
	}

	getTime(date: string): string {
		if (date === undefined) {
			return '';
		}
		return moment(date).format('HH:mm');
	}

	get f(): any {
		return this.passwordForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: PasswordEditDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: PasswordEditDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicDatepicker(): string {
		return StandardDynamicComponent.dynamicDatepicker(this.screenWidth);
	}

	dynamicInputTextDesktopTabletWidth(): string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth, 'w-20rem', 'w-30rem');
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

	dynamicInputTextFormat(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}
}
